import { useEffect, useState } from "react";

import { useLocalStorageState } from "../../utils/utils";

const PANEL_KEYS = {
  NetWorth: 1,
  YieldingPositions: 2,
  RewardOptions: 3,
  Balances: 4,
  DEXGrid: 5,
  LPGrid: 6,
  Staking: 7,
  FarmGrid: 8,
  YieldAggGrid: 9,
  MarginTrading: 10,
  ManagedFunds: 11,
  Lending: 12,
  ValidatorStaking: 13,
  NFTMarkets: 14,
  NFTGrid: 15,
};

export type CollapseControl = {
  getPanelKey: (componentName: keyof typeof PANEL_KEYS) => number;
  activePanels: number[];
  expandAllPanels: () => void;
  closeAllPanels: () => void;
  onPanelClick: (keys: string | string[]) => void;
};

export type CollapseControlProps = Omit<CollapseControl, "closeAllPanels" | "expandAllPanels">;

const panelKeysMapper = () => {
  const keyValues: number[] = [];
  for (const value of Object.values(PANEL_KEYS)) {
    keyValues.push(value);
  }
  return keyValues;
};

export const useCollapseControl = (): CollapseControl => {
  const [activePanels, setActivePanels] = useState<number[]>(panelKeysMapper());
  const [netWorthActiveKey, setNetWorthActiveKey] = useLocalStorageState("netWorthCollapseKeySetting", false);
  const [yieldActiveKey, setYieldActiveKey] = useLocalStorageState("yieldCollapseKeySetting", false);

  //only need to run once
  useEffect(() => {
    setPreviousStoredSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setPreviousStoredSettings = () => {
    const keys: number[] = [];
    if (netWorthActiveKey && yieldActiveKey) {
      keys.push(PANEL_KEYS.NetWorth, PANEL_KEYS.YieldingPositions);
    } else if (netWorthActiveKey) {
      keys.push(PANEL_KEYS.NetWorth);
    } else if (yieldActiveKey) {
      keys.push(PANEL_KEYS.YieldingPositions);
    }

    setActivePanels((prevState) => prevState.filter((state) => !keys.includes(state)));
  };

  const setLocalStorageKeys = (keys: number[]) => {
    let netWorthExists = false;
    let yieldActiveExists = false;

    keys.forEach((key) => {
      if (key === PANEL_KEYS.NetWorth) {
        netWorthExists = true;
      }
      if (key === PANEL_KEYS.YieldingPositions) {
        yieldActiveExists = true;
      }
    });
    setNetWorthActiveKey(!netWorthExists);
    setYieldActiveKey(!yieldActiveExists);
  };

  const expandAllPanels = () => {
    setActivePanels(panelKeysMapper());
  };

  const closeAllPanels = () => {
    setActivePanels([]);
  };

  const onPanelClick = (keys: string | string[]) => {
    // Keys shouldn't ever be a single string based on usage, unable to change the type being sent by ant-d
    if (typeof keys === "string") {
      return;
    }

    const mappedKeysToNumbers = keys.map((key) => Number(key));
    setLocalStorageKeys(mappedKeysToNumbers);
    setActivePanels(mappedKeysToNumbers);
  };

  const getPanelKey = (componentName: keyof typeof PANEL_KEYS) => PANEL_KEYS[componentName];

  return {
    getPanelKey,
    activePanels,
    expandAllPanels,
    closeAllPanels,
    onPanelClick,
  };
};
